import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { translateAgentContent, agentTranslationKeys } from 'utils/agent-translation';
import type { AgentTranslationContent } from 'utils/agent-translation';
import { SiteLanguage } from 'types/locales';

export interface UseAgentTranslationOptions {
  /**
   * The content to translate
   */
  content: AgentTranslationContent | null;
  
  /**
   * Agent ID for cache key (ensures same agent always uses cached translation)
   */
  agentId: string | null;
  
  /**
   * Target language code (e.g., 'fr-CA', 'es', 'de')
   */
  targetLanguage: string;
  
  /**
   * Source language code (defaults to 'en')
   */
  sourceLanguage?: string;
  
  /**
   * Whether translation should be enabled
   * If false, no translation will be attempted
   */
  enabled: boolean;
}

export interface UseAgentTranslationResult {
  /**
   * The translated content (or original if translation failed/not complete)
   */
  translatedContent: AgentTranslationContent | null;
  
  /**
   * Whether translation is currently in progress
   */
  isTranslating: boolean;
  
  /**
   * Whether translation completed successfully
   */
  isTranslated: boolean;
  
  /**
   * Whether translation failed or timed out
   */
  hasError: boolean;
  
  /**
   * Error message if translation failed
   */
  error: string | null;
  
  /**
   * Manually retry translation
   */
  refetch: () => void;
}

/**
 * Hook to translate agent profile content with automatic timeout fallback using react-query
 * 
 * This hook will attempt to translate agent content (bio, specializations, designations)
 * to the target language. If the translation takes longer than the timeout (default 2 seconds),
 * it will fall back to showing the original content.
 * 
 * Uses react-query for:
 * - Automatic caching (same content won't be translated twice)
 * - Request deduplication
 * - Proper loading and error states
 * - Built-in retry logic
 * 
 * @param options - Configuration options
 * @returns Translation state and controls
 * 
 * @example
 * ```tsx
 * const { translatedContent, isTranslating, isTranslated } = useAgentTranslation({
 *   content: {
 *     bio: agentInfo.bio,
 *     specializations: agentInfo.specializations,
 *     designations: agentInfo.designations,
 *   },
 *   targetLanguage: 'fr-CA',
 *   enabled: isNonDefaultLocale,
 * });
 * ```
 */
export function useAgentTranslation(
  options: UseAgentTranslationOptions
): UseAgentTranslationResult {
  const {
    content,
    agentId,
    targetLanguage,
    sourceLanguage = SiteLanguage.English,
    enabled,
  } = options;

  // Determine if we should actually query
  const shouldTranslate = useMemo(() => {
    if (!enabled || !content) return false;
    if (sourceLanguage === targetLanguage) return false;
    if (!content.bio && !content.specializations?.length && !content.designations?.length) return false;
    return true;
  }, [enabled, content, sourceLanguage, targetLanguage]);

  // Use react-query for translation with timeout handling
  const {
    data: translatedData,
    isLoading,
    isSuccess,
    isError,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: (content && agentId)
      ? agentTranslationKeys.translation(agentId, targetLanguage, sourceLanguage)
      : ['agent-translation', 'disabled'],
    queryFn: async ({ signal }) => {
      if (!content) {
        throw new Error('No content to translate');
      }
      const result = await translateAgentContent(content, targetLanguage, sourceLanguage, signal);
      return result;
    },
    enabled: shouldTranslate,
    // Prevent automatic background refetching
    staleTime: Infinity,
    // Cache successful translations indefinitely
    gcTime: Infinity,
    // Don't retry on timeout errors (fallback to original content instead)
    retry: (failureCount, error) => {
      // Don't retry timeout errors
      if (error instanceof Error && error.message.includes('timeout')) {
        return false;
      }
      // Retry network errors up to 1 time
      return failureCount < 1;
    },
    // Set a retry delay
    retryDelay: 1000,
    // Ensure query runs in background without blocking
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Determine the content to display
  const translatedContent = useMemo(() => {
    // If translation is disabled or not needed, return original content
    if (!shouldTranslate) {
      return content;
    }
    
    // If translation succeeded, return translated content
    if (isSuccess && translatedData) {
      return translatedData;
    }
    
    // If translation is still loading or failed, return original content as fallback
    return content;
  }, [shouldTranslate, isSuccess, translatedData, content]);

  return {
    translatedContent,
    isTranslating: isLoading,
    isTranslated: isSuccess && shouldTranslate,
    hasError: isError,
    error: queryError ? (queryError as Error).message : null,
    refetch: () => { refetch(); },
  };
}

